"use client";

import { useState, useCallback } from "react";
import {
  Upload,
  Loader2,
  Shield,
  CheckCircle,
  AlertCircle,
  X,
  FileText,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { ExtractionResult } from "@/lib/text-extractor";
import { AnonymizationResult, buildDeanonymizationMap } from "@/lib/anonymizer";
import { matchDocumentToPatient, type PatientForMatch, type MatchResult } from "@/lib/patient-declensions";
import { runIdentifyAndAnonymize, DOCUMENT_CONTEXT_MAX_LENGTH } from "@/lib/identify-and-anonymize";

export type DocStatus =
  | "pending"
  | "extracting"
  | "anonymizing"
  | "matching"
  | "done"
  | "error";

export interface ProcessedDoc {
  id: string;
  file: File;
  status: DocStatus;
  error?: string;
  extractionResult?: ExtractionResult;
  declensions?: { format: string; value: string }[];
  anonymized?: AnonymizationResult;
  docBirthDate?: string | null;
  matchResult?: MatchResult;
  displayName?: string;
}

interface BatchPatientDocumentDropZoneProps {
  practitioners?: string[];
  existingPatients: PatientForMatch[];
  onPatientsRefresh?: () => void;
  className?: string;
}

const MAX_FILES = 50;

export function BatchPatientDocumentDropZone({
  practitioners = [],
  existingPatients,
  onPatientsRefresh,
  className,
}: BatchPatientDocumentDropZoneProps) {
  const [docs, setDocs] = useState<ProcessedDoc[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [importSummary, setImportSummary] = useState<string | null>(null);

  const addFiles = useCallback((fileList: FileList | null) => {
    if (!fileList?.length) return;
    const files = Array.from(fileList).filter(
      (f) =>
        f.type.startsWith("image/") ||
        f.name.toLowerCase().endsWith(".pdf") ||
        f.name.toLowerCase().endsWith(".doc") ||
        f.name.toLowerCase().endsWith(".docx")
    );
    const newDocs: ProcessedDoc[] = files.slice(0, MAX_FILES - docs.length).map((file) => ({
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
      file,
      status: "pending",
    }));
    setDocs((prev) => [...prev, ...newDocs].slice(0, MAX_FILES));
    setGlobalError(null);
    setImportSummary(null);
  }, [docs.length]);

  const removeDoc = useCallback((id: string) => {
    setDocs((prev) => prev.filter((d) => d.id !== id));
  }, []);

  const processOne = useCallback(
    async (doc: ProcessedDoc): Promise<ProcessedDoc> => {
      try {
        setDocs((prev) =>
          prev.map((d) => (d.id === doc.id ? { ...d, status: "extracting" as const } : d))
        );
        const identifyResult = await runIdentifyAndAnonymize({
          file: doc.file,
          practitioners,
          documentContextMaxLength: DOCUMENT_CONTEXT_MAX_LENGTH,
          includeOtherNamesInAnonymization: true,
        });

        if ("error" in identifyResult) {
          return {
            ...doc,
            status: "error",
            error: identifyResult.error,
            extractionResult: identifyResult.extraction,
          };
        }

        const { extraction: result, identifiedResults: resultsWithIds, anonymized } = identifyResult;

        setDocs((prev) =>
          prev.map((d) => (d.id === doc.id ? { ...d, status: "matching" as const } : d))
        );

        let docBirthDate: string | null = null;
        try {
          const birthRes = await fetch("/api/extract-birthdate", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ anonymizedText: anonymized.anonymizedText }),
          });
          if (birthRes.ok) {
            const birthData = await birthRes.json();
            if (birthData.birthdate) {
              const [d, m, y] = birthData.birthdate.split("/");
              if (d && m && y)
                docBirthDate = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
            }
          }
        } catch {
          // ignore
        }

        const docDeclensionValues = resultsWithIds.map((r) => r.value).filter(Boolean);
        const matchResult = matchDocumentToPatient(
          docDeclensionValues,
          docBirthDate,
          existingPatients
        );

        const nom = resultsWithIds.find((r) => r.format === "NOM")?.value;
        const prenom = resultsWithIds.find((r) => r.format === "Prénom")?.value;
        const displayName = [nom, prenom].filter(Boolean).join(" ") || "—";

        return {
          ...doc,
          status: "done",
          extractionResult: result,
          declensions: resultsWithIds,
          anonymized,
          docBirthDate,
          matchResult,
          displayName,
        };
      } catch (err) {
        return {
          ...doc,
          status: "error",
          error: err instanceof Error ? err.message : "Erreur",
        };
      }
    },
    [existingPatients, practitioners]
  );

  const processAll = useCallback(async () => {
    const pending = docs.filter((d) => d.status === "pending");
    if (!pending.length) return;
    setGlobalError(null);
    for (const doc of pending) {
      const updated = await processOne(doc);
      setDocs((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    }
  }, [docs, processOne]);

  const importAll = useCallback(async () => {
    setIsImporting(true);
    setGlobalError(null);
    setImportSummary(null);

    // D'abord, traiter tous les documents en attente
    const localDocs = [...docs];
    for (let i = 0; i < localDocs.length; i++) {
      if (localDocs[i].status === "pending") {
        const updated = await processOne(localDocs[i]);
        localDocs[i] = updated;
        setDocs(prev => prev.map(d => d.id === updated.id ? updated : d));
      }
    }

    // Ensuite, importer ceux qui sont prêts
    const done = localDocs.filter((d) => d.status === "done" && d.anonymized && d.declensions);
    if (!done.length) {
      setIsImporting(false);
      return;
    }

    let created = 0;
    let linked = 0;
    let errors = 0;

    for (const doc of done) {
      if (!doc.anonymized || !doc.declensions) continue;
      try {
        const extractRes = await fetch("/api/patients/extract-from-document", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ anonymizedText: doc.anonymized.anonymizedText }),
        });
        if (!extractRes.ok) throw new Error("Extraction");
        const extracted = await extractRes.json();

        const nom = doc.declensions.find((r) => r.format === "NOM")?.value ?? "Inconnu";
        const prenom = doc.declensions.find((r) => r.format === "Prénom")?.value ?? "Inconnu";
        const declensions = doc.declensions.map((r) => r.value).filter(Boolean);

        const deanonymMap = buildDeanonymizationMap(doc.anonymized.replacements);
        if (doc.matchResult?.matchedPatientId) {
          const patientId = doc.matchResult.matchedPatientId;
          if (extracted.notes) {
            await fetch(`/api/patients/${patientId}`, {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                notes: extracted.notes,
                nameDeclensions: declensions,
                notesAnonymizationMap: Object.keys(deanonymMap).length > 0 ? deanonymMap : undefined,
              }),
            });
          }
          for (const patho of extracted.pathologies || []) {
            await fetch(`/api/patients/${patientId}/pathologies`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                name: patho.name,
                description: patho.description ?? undefined,
              }),
            });
          }
          const reportRes = await fetch(`/api/patients/${patientId}/reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              title: `Rapport importé - ${doc.file.name}`,
              content: doc.anonymized.anonymizedText.substring(0, 5000),
              summary: extracted.reportSummary ?? undefined,
              improvements: extracted.reportImprovements ?? undefined,
              actionsToCome: extracted.reportActionsToCome ?? undefined,
              reportDate: new Date().toISOString().split("T")[0],
              anonymizationMap: Object.keys(deanonymMap).length > 0 ? deanonymMap : undefined,
            }),
          });
          if (!reportRes.ok) {
            const errData = await reportRes.json().catch(() => ({}));
            throw new Error(errData?.error ?? "Erreur création du rapport");
          }
          const created = await reportRes.json();
          const formData = new FormData();
          formData.append("file", doc.file);
          await fetch(`/api/patients/${patientId}/reports/${created.id}/upload-original`, {
            method: "POST",
            body: formData,
          });
          linked++;
        } else {
          const createRes = await fetch("/api/patients", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              firstName: prenom,
              lastName: nom,
              birthDate: extracted.birthDate ?? undefined,
              notes: extracted.notes ?? undefined,
              nameDeclensions: declensions,
              notesAnonymizationMap: Object.keys(deanonymMap).length > 0 ? deanonymMap : undefined,
            }),
          });
          if (!createRes.ok) throw new Error("Création patient");
          const createdPatient = await createRes.json();
          for (const patho of extracted.pathologies || []) {
            await fetch(`/api/patients/${createdPatient.id}/pathologies`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                name: patho.name,
                description: patho.description ?? undefined,
              }),
            });
          }
          const reportRes = await fetch(`/api/patients/${createdPatient.id}/reports`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              title: `Rapport importé - ${doc.file.name}`,
              content: doc.anonymized.anonymizedText.substring(0, 5000),
              summary: extracted.reportSummary ?? undefined,
              improvements: extracted.reportImprovements ?? undefined,
              actionsToCome: extracted.reportActionsToCome ?? undefined,
              reportDate: new Date().toISOString().split("T")[0],
              anonymizationMap: Object.keys(deanonymMap).length > 0 ? deanonymMap : undefined,
            }),
          });
          if (!reportRes.ok) {
            const errData = await reportRes.json().catch(() => ({}));
            throw new Error(errData?.error ?? "Erreur création du rapport");
          }
          const createdReport = await reportRes.json();
          const formData = new FormData();
          formData.append("file", doc.file);
          await fetch(`/api/patients/${createdPatient.id}/reports/${createdReport.id}/upload-original`, {
            method: "POST",
            body: formData,
          });
          created++;
        }
      } catch {
        errors++;
      }
    }

    setImportSummary(
      `Import terminé : ${linked} document(s) lié(s), ${created} nouveau(x) patient(s), ${errors} erreur(s).`
    );
    onPatientsRefresh?.();
    setIsImporting(false);
  }, [docs, onPatientsRefresh]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles]
  );

  const pendingCount = docs.filter((d) => d.status === "pending").length;
  const doneCount = docs.filter((d) => d.status === "done").length;
  const canProcess = pendingCount > 0;
  const canImport = doneCount > 0 && !isImporting;

  return (
    <div className={cn("space-y-4", className)}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={cn(
          "border-2 border-dashed rounded-2xl p-6 text-center transition-all",
          isDragging ? "border-primary-500 bg-primary-50/50" : "border-slate-300 bg-white/40"
        )}
      >
        <input
          type="file"
          accept=".pdf,.doc,.docx,image/*"
          multiple
          onChange={(e) => addFiles(e.target.files)}
          className="absolute w-0 h-0 opacity-0"
          id="batch-patient-files"
        />
        <label
          htmlFor="batch-patient-files"
          className="cursor-pointer flex flex-col items-center gap-2"
        >
          <Upload className="h-10 w-10 text-primary-600" />
          <p className="text-sm font-semibold text-slate-800">
            Déposer un ou plusieurs documents (PDF, Word, images)
          </p>
          <p className="text-xs text-slate-500">
            Un seul fichier ou jusqu&apos;à {MAX_FILES} fichiers. Liste puis traitement.
          </p>
        </label>
      </div>

      {globalError && (
        <div className="rounded-xl bg-error-50 border border-error-200 px-4 py-3 text-sm text-error-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {globalError}
        </div>
      )}
      {importSummary && (
        <div className="rounded-xl bg-success-50 border border-success-200 px-4 py-3 text-sm text-success-700 flex items-center gap-2">
          <CheckCircle className="h-4 w-4 shrink-0" />
          {importSummary}
        </div>
      )}

      {docs.length > 0 && (
        <div className="rounded-2xl bg-white/60 backdrop-blur-xl border border-white/50 shadow-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200/80 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary-600" />
              <span className="font-semibold text-slate-900">Documents ({docs.length})</span>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={importAll}
                disabled={!canProcess && !canImport}
                className={cn(
                  "rounded-xl px-3 py-2 text-sm font-medium flex items-center gap-2",
                  (canProcess || canImport)
                    ? "bg-primary-600 text-white hover:bg-primary-700"
                    : "bg-slate-200 text-slate-500 cursor-not-allowed"
                )}
              >
                {isImporting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Traitement en cours…
                  </>
                ) : (
                  <>
                    <User className="h-4 w-4" />
                    Importer
                  </>
                )}
              </button>
            </div>
          </div>
          <ul className="divide-y divide-slate-200/80 max-h-80 overflow-y-auto">
            {docs.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50/80"
              >
                <FileText className="h-5 w-5 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">{doc.file.name}</p>
                  <p className="text-xs text-slate-500">
                    {doc.status === "pending" && "En attente"}
                    {doc.status === "extracting" && "Extraction…"}
                    {doc.status === "anonymizing" && "Anonymisation…"}
                    {doc.status === "matching" && "Matching…"}
                    {doc.status === "done" &&
                      (doc.displayName
                        ? `${doc.displayName} — ${doc.matchResult?.matchedPatientId ? "Existant" : "Nouveau"}`
                        : "Prêt")}
                    {doc.status === "error" && (doc.error || "Erreur")}
                  </p>
                </div>
                {(doc.status === "extracting" ||
                  doc.status === "anonymizing" ||
                  doc.status === "matching") && (
                    <Loader2 className="h-4 w-4 animate-spin text-primary-600 shrink-0" />
                  )}
                {doc.status === "done" && (
                  <CheckCircle className="h-4 w-4 text-success-600 shrink-0" />
                )}
                {doc.status === "error" && (
                  <AlertCircle className="h-4 w-4 text-error-500 shrink-0" />
                )}
                <button
                  type="button"
                  onClick={() => removeDoc(doc.id)}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-600 shrink-0"
                  aria-label="Retirer"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
